import React, { useState, useEffect, useRef } from "react";
import Loader from "../../components/ui/Loader/Loader";
import Status from "../../components/ui/Status";
import HttpAPICall from "../../services/HttpAPICall";
import { BACKOFFICE_BASE_URL } from "../../services/TapApiUrls";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import CountUpdate from "./countUpdate";
import TapIcon from "../../services/TapIcon";
import SubModuleUpdatePopup from "./SubModuleUpdatePopup";
import { DisplayListPagination } from "../../components/TapUi";
import ModuleUpdatePopup from "./ModuleUpdatePopup";


const COUNT_FIELDS = [
    { key: "last_asset_count", label: "Assets" },
    { key: "last_sites_count", label: "Sites" },
    { key: "last_users_count", label: "Users" },
    { key: "last_items_count", label: "Items" },
    { key: "last_warehouses_count", label: "Warehouses" },
    { key: "last_deals_count", label: "Deals" },
    { key: "last_employees_count", label: "Employees" },
];

const GroupDetail = (props) => {
    const moduleUpdatePopupRef = useRef(null);
    const access_token = useSelector(state => state.auth.access_token);
    const [moduleData, setModuleData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const subModuleUpdatePopupRef = useRef(null);
    const subModuleEditRef = useRef(null);
    const [selectedSubModules, setSelectedSubModules] = useState([]);
    const [selectAllChecked, setSelectAllChecked] = useState(false);
    const [currentModuleId, setCurrentModuleId] = useState(null);
    const [currentTapMasterId, setCurrentTapMasterId] = useState(null);
    const [logsData, setLogsData] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [listingMeta, setListingMeta] = useState(null);

    const [activeTab, setActiveTab] = useState("details");

    useEffect(() => {
        if (props.groupDetail?.master_id) {
            fetchModuleDetails();
        }
    }, [props.groupDetail]);

    const fetchModuleDetails = () => {
        setLoading(true);
        const id = props.groupDetail.master_id;

        HttpAPICall.withAthorization(
            'GET',
            BACKOFFICE_BASE_URL + '/group/module_detail/' + id,
            access_token,
            {},
            {},
            (resp) => {
                if (resp && Array.isArray(resp.data?.data)) {
                    setModuleData(resp.data?.data);
                } else {
                    setModuleData([]);
                }
            }
        ).finally(() => setLoading(false));
    };

    const fetchModuleLogs = (page = 1) => {
        if (!props.groupDetail?.master_id) return;

        setLogsLoading(true);
        const params = { page: page, per_page: 100 };
        HttpAPICall.withAthorization(
            'GET',
            BACKOFFICE_BASE_URL + '/group/module_log/' + props.groupDetail.master_id,
            access_token,
            params,
            {},
            (resp) => {
                if (resp && Array.isArray(resp.data?.data)) {
                    setLogsData(resp.data.data);
                    setListingMeta(resp.data);
                } else {
                    setLogsData([]);
                    setListingMeta(null);
                }
            },
            (error) => {
                toast.error('Failed to load logs', {
                    position: toast.POSITION.TOP_RIGHT
                });
                setLogsData([]);
                setListingMeta(null);
            }
        ).finally(() => setLogsLoading(false));
    };

    useEffect(() => {
        if (activeTab === "logs" && props.groupDetail?.master_id) {
            fetchModuleLogs(1);
        }
    }, [activeTab, props.groupDetail]);

    const handleEditModule = (module) => {
        if (!moduleUpdatePopupRef.current) return;

        let counts = {};
        switch (module.module) {
            case 'iam':
                counts = {
                    total_assets: module.assets ?? 0,
                    total_users: module.users ?? 0,
                    total_sites: module.sites ?? 0,
                };
                break;
            case 'isc':
                counts = {
                    total_warehouses: module.warehouses ?? 0,
                    total_items: module.items ?? 0,
                };
                break;
            case 'hrm':
                counts = {
                    total_employees: module.employees ?? 0,
                };
                break;
            case 'scrm':
                counts = {
                    total_deals: module.deals ?? 0,
                };
                break;
            default:
                counts = {};
        }

        moduleUpdatePopupRef.current.openEditModal({
            id: module.id,
            name: getModuleDisplayName(module.module),
            module_key: module.module,
            tap_master_id: props.groupDetail?.master_id,
            from: module.from,
            till: module.till,
            counts,
        });
    };
    const handleModuleUpdateSuccess = () => {
        fetchModuleDetails();
    };
    const handleSubModuleStatusUpdate = (moduleId, subModuleId, isChecked) => {
        const newStatus = isChecked ? 1 : 0;
        setUpdatingStatus(true);

        HttpAPICall.withAthorization(
            'PUT',
            BACKOFFICE_BASE_URL + '/group/submodule/status/' + subModuleId,
            access_token,
            {},
            { status: newStatus },
            (resp) => {
                toast.success('Sub-module status updated successfully', {
                    position: toast.POSITION.TOP_RIGHT
                });
                fetchModuleDetails();
                setUpdatingStatus(false);
            },
            (error) => {
                toast.error('Failed to update sub-module status', {
                    position: toast.POSITION.TOP_RIGHT
                });
                setUpdatingStatus(false);
            }
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        const day = date.getDate();
        const month = date.toLocaleString('default', { month: 'short' });
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const handleIamCountUpdate = () => fetchModuleDetails();
    const handleInventoryCountUpdate = () => fetchModuleDetails();
    const handleHrmCountUpdate = () => fetchModuleDetails();
    const handleCrmDealCountUpdate = () => fetchModuleDetails();

    const handleEditIAMCount = () => {
        const iamModule = moduleData.find(item => item.module === 'iam');
        if (!iamModule) return;

        if (subModuleEditRef.current) {
            subModuleEditRef.current.openEditModal('iam', {
                total_assets: iamModule.assets ?? 0,
                total_users: iamModule.users ?? 0,
                total_sites: iamModule.sites ?? 0,
                module_id: iamModule.id,
                tap_master_id: props.groupDetail?.master_id,
            });
        }
    };

    const handleEditInventoryCount = () => {
        const inventoryModule = moduleData.find(item => item.module === 'isc');
        if (!inventoryModule) return;

        if (subModuleEditRef.current) {
            subModuleEditRef.current.openEditModal('inventory', {
                total_warehouses: inventoryModule.warehouses ?? 0,
                total_items: inventoryModule.items ?? 0,
                module_id: inventoryModule.id,
                tap_master_id: props.groupDetail?.master_id,
            });
        }
    };

    const handleEditHrmCount = () => {
        const hrmModule = moduleData.find(item => item.module === 'hrm');
        if (!hrmModule) return;

        if (subModuleEditRef.current) {
            subModuleEditRef.current.openEditModal('hrm', {
                total_employees: hrmModule.employees ?? 0,
                module_id: hrmModule.id,
                tap_master_id: props.groupDetail?.master_id,
            });
        }
    };

    const handleEditCrmDealCount = () => {
        const dealModule = moduleData.find(item => item.module === 'scrm');
        if (!dealModule) return;

        if (subModuleEditRef.current) {
            subModuleEditRef.current.openEditModal('crm_deal', {
                total_deals: dealModule.deals ?? 0,
                module_id: dealModule.id,
                tap_master_id: props.groupDetail?.master_id,
            });
        }
    };

    const handleSubModuleCheckbox = (subModuleId, moduleId) => {
        setSelectedSubModules(prev => {
            const exists = prev.includes(subModuleId);
            let newSelection;

            if (exists) {
                newSelection = prev.filter(id => id !== subModuleId);
            } else {
                newSelection = [...prev, subModuleId];
            }

            const module = moduleData.find(m => m.id === moduleId);
            if (module && module.sub_modules) {
                const allSubModuleIds = module.sub_modules.map(item => item.id);
                setSelectAllChecked(newSelection.length === allSubModuleIds.length && allSubModuleIds.length > 0);
            }

            setCurrentModuleId(moduleId);
            setCurrentTapMasterId(props.groupDetail?.master_id);
            return newSelection;
        });
    };

    const handleMultipleSubModules = (module) => {
        if (selectedSubModules.length === 0) {
            toast.warning("Please select at least one sub-module", {
                position: toast.POSITION.TOP_RIGHT
            });
            return;
        }

        const selectedSubModuleObjects = module.sub_modules
            .filter(sub => selectedSubModules.includes(sub.id))
            .map(sub => ({
                ...sub,
                module_id: module.id,
                tap_master_id: props.groupDetail?.master_id,
            }));

        if (subModuleUpdatePopupRef.current && selectedSubModuleObjects.length > 0) {
            subModuleUpdatePopupRef.current.openEditModal(selectedSubModuleObjects);
        }
    };

    const renderModuleCounts = (module) => {
        switch (module.module) {
            case 'iam':
                return (
                    <>
                        <div className="tab_content_wrapper mt-3">
                            <div className="d-flex justify-content-between align-items-center w-100">
                                <span className="content_heading">Asset Module</span>
                                <span className="dropdown">
                                    <button type="button" id='actionMenuButton' data-bs-toggle="dropdown" aria-expanded="false" disabled={updatingStatus} className="btn btn-secondary">
                                        <TapIcon.imageIcon icon={TapIcon.ActionIcon} alt="action-icon" className="img-fluid" />
                                    </button>
                                    <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton1">
                                        <li><button onClick={handleEditIAMCount} className='dropdown-item'>Update Count</button></li>
                                        <li>
                                            <button className="dropdown-item" onClick={() => handleMultipleSubModules(module)} disabled={selectedSubModules.length === 0}>
                                                Multiple Sub-Module
                                            </button>
                                        </li>
                                    </ul>
                                </span>
                            </div>
                        </div>
                        <div className="table-responsive">
                            <table className="table table-hover table-bordered bg-white mt-2">
                                <tbody>
                                    <tr><td style={{ width: "30%" }}>Asset</td><td style={{ width: "70%" }}><strong>{module.assets ?? 0}</strong></td></tr>
                                    <tr><td>User</td><td><strong>{module.users ?? 0}</strong></td></tr>
                                    <tr><td>Site</td><td><strong>{module.sites ?? 0}</strong></td></tr>
                                </tbody>
                            </table>
                        </div>
                    </>
                );

            case 'isc':
                return (
                    <>
                        <div className="tab_content_wrapper mt-3">
                            <div className="d-flex justify-content-between align-items-center w-100">
                                <span className="content_heading">Inventory Module</span>
                                <span className="dropdown">
                                    <button type="button" id='actionMenuButton' data-bs-toggle="dropdown" aria-expanded="false" disabled={updatingStatus} className="btn btn-secondary">
                                        <TapIcon.imageIcon icon={TapIcon.ActionIcon} alt="action-icon" className="img-fluid" />
                                    </button>
                                    <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton1">
                                        <li><button onClick={handleEditInventoryCount} className='dropdown-item'>Update Count</button></li>
                                        <li>
                                            <button className="dropdown-item" onClick={() => handleMultipleSubModules(module)} disabled={selectedSubModules.length === 0}>
                                                Multiple Sub-Module
                                            </button>
                                        </li>
                                    </ul>
                                </span>
                            </div>
                        </div>
                        <div className="table-responsive mt-2">
                            <table className="table table-hover table-bordered bg-white my-2">
                                <tbody>
                                    <tr><td style={{ width: "30%" }}>Warehouse</td><td style={{ width: "70%" }}><strong>{module.warehouses ?? 0}</strong></td></tr>
                                    <tr><td>Item</td><td><strong>{module.items ?? 0}</strong></td></tr>
                                </tbody>
                            </table>
                        </div>
                    </>
                );

            case 'hrm':
                return (
                    <>
                        <div className="tab_content_wrapper mt-3">
                            <div className="d-flex justify-content-between align-items-center w-100">
                                <span className="content_heading">HRM Module</span>
                                <span className="dropdown">
                                    <button type="button" id='actionMenuButton' data-bs-toggle="dropdown" aria-expanded="false" disabled={updatingStatus} className="btn btn-secondary">
                                        <TapIcon.imageIcon icon={TapIcon.ActionIcon} alt="action-icon" className="img-fluid" />
                                    </button>
                                    <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton1">
                                        <li><button onClick={handleEditHrmCount} className='dropdown-item'>Update Count</button></li>
                                        <li>
                                            <button className="dropdown-item" onClick={() => handleMultipleSubModules(module)} disabled={selectedSubModules.length === 0}>
                                                Multiple Sub-Module
                                            </button>
                                        </li>
                                    </ul>
                                </span>
                            </div>
                        </div>
                        <div className="table-responsive mt-2">
                            <table className="table table-hover table-bordered bg-white my-2">
                                <tbody>
                                    <tr><td style={{ width: "30%" }}>Employee</td><td style={{ width: "70%" }}><strong>{module.employees ?? 0}</strong></td></tr>
                                </tbody>
                            </table>
                        </div>
                    </>
                );

            case 'scrm':
                return (
                    <>
                        <div className="tab_content_wrapper mt-3">
                            <div className="d-flex justify-content-between align-items-center w-100">
                                <span className="content_heading">Sales CRM Module</span>
                                <span className="dropdown">
                                    <button type="button" id='actionMenuButton' data-bs-toggle="dropdown" aria-expanded="false" disabled={updatingStatus} className="btn btn-secondary">
                                        <TapIcon.imageIcon icon={TapIcon.ActionIcon} alt="action-icon" className="img-fluid" />
                                    </button>
                                    <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton1">
                                        <li><button onClick={handleEditCrmDealCount} className='dropdown-item'>Update Count</button></li>
                                        <li>
                                            <button className="dropdown-item" onClick={() => handleMultipleSubModules(module)} disabled={selectedSubModules.length === 0}>
                                                Multiple Sub-Module
                                            </button>
                                        </li>
                                    </ul>
                                </span>
                            </div>
                        </div>
                        <div className="table-responsive mt-2">
                            <table className="table table-hover table-bordered bg-white my-2">
                                <tbody>
                                    <tr><td style={{ width: "30%" }}>Deal</td><td style={{ width: "70%" }}><strong>{module.deals ?? 0}</strong></td></tr>
                                </tbody>
                            </table>
                        </div>
                    </>
                );

            default:
                return null;
        }
    };

    const getModuleDisplayName = (moduleKey) => {
        switch (moduleKey) {
            case 'iam': return 'Asset Module';
            case 'isc': return 'Inventory Module';
            case 'hrm': return 'HR Module';
            case 'scrm': return 'Sales Module';
            default: return moduleKey;
        }
    };

    const renderSubModules = (module) => {
        if (!module.sub_modules || module.sub_modules.length === 0) {
            return null
        }
        const allSubModuleIds = module.sub_modules.map(item => item.id);
        const allChecked = allSubModuleIds.length > 0 && allSubModuleIds.every(id => selectedSubModules.includes(id));

        return (
            <div className="table-responsive">
                <table className="table table-hover table-bordered bg-white mb-2 ">
                    <thead className="table-light">
                        <tr>
                            <th style={{ width: "5%" }}>
                                <input
                                    type="checkbox"
                                    checked={allChecked}
                                    onChange={() => {
                                        if (allChecked) {
                                            setSelectedSubModules(prev => prev.filter(id => !allSubModuleIds.includes(id)));
                                        } else {
                                            setSelectedSubModules(prev => [...prev, ...allSubModuleIds]);
                                        }
                                        setCurrentModuleId(module.id);
                                        setCurrentTapMasterId(props.groupDetail?.master_id);
                                    }}
                                />
                            </th>
                            <th style={{ width: "25%" }}>Module / Sub-Module</th>
                            <th className="text-center" style={{ width: "20%" }}>Due Date</th>
                            <th className="text-center" style={{ width: "20%" }}>Status</th>
                            <th className="text-center" style={{ width: "10%" }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td></td>
                            <td>{getModuleDisplayName(module.module)}</td>
                            <td className="text-center">{formatDate(module.till)}</td>
                            <td className="text-center">
                                <span>
                                    {module?.status ? <Status color={module.status?.status_color_code}>{module.status?.status_text}</Status> : "-"}
                                </span>
                            </td>
                            <td className='text-center'>
                                <TapIcon.imageIcon
                                    icon={TapIcon.ListActionIcon}
                                    alt="action-icon"
                                    className="img-fluid px-3"
                                    data-bs-toggle="dropdown"
                                    href="#"
                                    role="button"
                                    aria-expanded="false"
                                />
                                <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton1">
                                    <li onClick={() => handleEditModule(module)}>
                                        <a className="dropdown-item" role="button">Edit</a>
                                    </li>
                                </ul>
                            </td>
                        </tr>

                        {module.sub_modules.map((subModule) => (
                            <tr key={subModule.id}>
                                <td>
                                    <input
                                        type="checkbox"
                                        checked={selectedSubModules.some(item => item === subModule.id)}
                                        onChange={() => handleSubModuleCheckbox(subModule.id, module.id)}
                                    />
                                </td>
                                <td>{subModule.name}</td>
                                <td className="text-center">{formatDate(subModule.till)}</td>
                                <td className="text-center">
                                    <span>
                                        {subModule?.status ?
                                            <Status color={subModule.status?.status_color_code}>{subModule.status?.status_text}</Status> : "-"}

                                    </span>
                                </td>
                                <td className='text-center'>
                                    <TapIcon.imageIcon icon={TapIcon.ListActionIcon} alt="action-icon" className="img-fluid px-3" data-bs-toggle="dropdown" href="#" role="button" aria-expanded="false" />
                                    <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton1">
                                        <li onClick={() => handleEditSubModule(subModule, module.id)}>
                                            <a className="dropdown-item" role="button">Edit</a>
                                        </li>
                                    </ul>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const handleSubModuleUpdateSuccess = () => {
        fetchModuleDetails();
        setSelectedSubModules([]);
        setSelectAllChecked(false);
    };

    const handleEditSubModule = (subModule, module_id) => {
        console.log(subModule);

        if (subModuleUpdatePopupRef.current) {
            subModuleUpdatePopupRef.current.openEditModal({
                id: subModule.id,
                name: subModule.name,
                module_id: module_id,
                tap_master_id: props.groupDetail?.master_id,
                from: subModule.from,
                till: subModule.till,
            });
        }
    };

    const renderModuleSection = (module) => {
        if (module.module === 'group') return null;

        return (
            <div key={module.id} className="module-section">
                {renderModuleCounts(module)}
                {renderSubModules(module)}
            </div>
        );
    };

    const renderCountChanges = (log) => {
        const rows = COUNT_FIELDS
            .filter(f => log[f.key] !== null && log[f.key] !== undefined)
            .map(f => (
                <div key={f.key} className="d-flex justify-content-between small">
                    <span className="text-muted">{f.label}:</span>
                    <strong className="ms-2">{log[f.key]}</strong>
                </div>
            ));

        if (rows.length === 0) return null;

        return <div className="mt-1">{rows}</div>;
    };

    const renderLogsTab = () => {
        if (logsLoading) {
            return <Loader />;
        }

        return (
            <>
                <div className="table-responsive mt-3">
                    <table className="table table-hover table-bordered bg-white my-2 align-middle">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: "4%" }}>#</th>
                                <th style={{ width: "15%" }}>Module</th>
                                <th style={{ width: "15%" }}>Sub-Module</th>
                                <th style={{ width: "15%" }}>Last Count</th>
                                <th style={{ width: "15%" }} className="text-center">Last Due Date</th>
                                <th style={{ width: "25%" }}>Notes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logsData.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center">
                                        No logs available
                                    </td>
                                </tr>
                            ) : (
                                logsData.map((log, index) => (
                                    <tr key={log.id ?? index}>
                                        <td>
                                            {listingMeta
                                                ? (listingMeta.current_page - 1) * listingMeta.per_page + index + 1
                                                : index + 1}
                                        </td>
                                        <td>{log.module?.name?.trim() || '-'}</td>
                                        <td>{log.sub_module?.name || '-'}</td>
                                        <td>{renderCountChanges(log) || <span className="text-muted">—</span>}</td>
                                        <td className="text-center">
                                            {log?.last_till_date ? formatDate(log.last_till_date) : "-"}
                                        </td>
                                        <td style={{ whiteSpace: "pre-wrap", fontSize: "13px" }}>
                                            {log.notes || '-'}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {listingMeta && listingMeta.last_page > 1 && (
                    <DisplayListPagination
                        meta={listingMeta}
                        onPageChange={(e) => fetchModuleLogs(e.selected + 1)}
                    />
                )}
            </>
        );
    };

    return (
        <>
            <CountUpdate
                ref={subModuleEditRef}
                onIamCountUpdate={handleIamCountUpdate}
                onInventoryCountUpdate={handleInventoryCountUpdate}
                onHrmCountUpdate={handleHrmCountUpdate}
                onCrmDealCountUpdate={handleCrmDealCountUpdate}
            />
            <SubModuleUpdatePopup
                ref={subModuleUpdatePopupRef}
                onUpdateSuccess={handleSubModuleUpdateSuccess}
            />
            <ModuleUpdatePopup
                ref={moduleUpdatePopupRef}
                onUpdateSuccess={handleModuleUpdateSuccess}
            />

            {props.detail_loading || loading ? <Loader /> :
                <section className="item_view_container bg-white border-0">
                    <div className="tab_content_header pt-0">
                        <ul className="nav nav-tabs mb-3" role="tablist">
                            <li className="nav-item" role="presentation">
                                <button
                                    type="button"
                                    className={`nav-link ${activeTab === "details" ? "active" : ""}`}
                                    onClick={() => setActiveTab("details")}
                                >
                                    Module Details
                                </button>
                            </li>
                            <li className="nav-item" role="presentation">
                                <button
                                    type="button"
                                    className={`nav-link ${activeTab === "logs" ? "active" : ""}`}
                                    onClick={() => setActiveTab("logs")}
                                >
                                    Logs
                                </button>
                            </li>
                        </ul>

                        {activeTab === "details" && (
                            <>
                                <table className="table table-hover table-bordered bg-white my-2">
                                    <tbody>
                                        <tr>
                                            <td style={{ width: "25%" }}>Group Name</td>
                                            <th>{props.groupDetail?.group_name || '-'}</th>
                                        </tr>
                                        <tr>
                                            <td style={{ width: "25%" }}>Group ID</td>
                                            <th>{props.groupDetail?.master?.tap_master_id || '-'}</th>
                                        </tr>
                                    </tbody>
                                </table>

                                {["iam", "isc", "hrm", "scrm"].map((key) => {
                                    const module = moduleData.find(m => m.module === key);
                                    return module ? renderModuleSection(module) : null;
                                })}
                            </>
                        )}

                        {activeTab === "logs" && renderLogsTab()}

                    </div>
                </section>
            }
        </>
    );
};

export default GroupDetail;